import React, { useCallback, useEffect, useState } from 'react';
import G17Client from '../../../services/G17Client';
import { getSDGImpact } from '../outputs';
import GenerateCharts from './GenerateCharts';
import { ReportData } from '../../../types/reportData';
import { GenderSplitDataSource } from './data/GenderSplit';
import { GenderSplitPcDataSource } from './data/GenderSplitPc';
import { GHGEmissionsDataSource } from './data/GHGEmissions';
import { LivingWageDataSource } from './data/LivingWage';
import { PayGapDataSource } from './data/PayGap';
import { GenderPayGapDataSource } from './data/GenderPayGap';
import { BonusesDataSource } from './data/Bonuses';
import { BoardGenderDataSource } from './data/BoardGender';
import { BoardDiversityDataSource } from './data/BoardDiversity';
import { PoliciesDataSource } from './data/Policies';
import Loader from '../../loader';
import { DownloadButton } from '../../button/DownloadButton';
import { getAnalytics } from '../../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../../services/analytics/AnalyticsEvents';
import { DownloadSettingModal, DownloadSettingPropType } from '../../downloads/DownloadSettingModal';
import { convertToUtrvStatuses, DownloadCsv, DownloadPptx, downloadReportHandler, DownloadSettingsType, DownloadSimple, DownloadXlsx } from '../../downloads/util/downloadReportHandler';
import { UtrvStatus } from '../../../constants/status';
import { DownloadMultiScope, VisibilityStatus } from '../../../types/download';
import { handleSettingsChange } from '../../downloads';
import { getDocVisibility } from '../sgx-metric-report/downloadScope';
import { ScopeGroups } from '../../../model/surveyData';
import { reportMultiScopeData } from '../reportApi';

interface Props {
  initiativeId: string;
  surveyId: string;
  publicQuestionsOnly?: boolean;
  utrvStatuses?: UtrvStatus[];
  downloadXlsx?: DownloadXlsx;
  downloadPptx?: DownloadPptx;
  downloadCsv?: DownloadCsv;
  downloadSimple?: DownloadSimple;
  defaultStatus?: UtrvStatus;
  totals?: {
    total: number;
    verified: number;
    sentToAssurer: number;
    assured: number;
  };
}

export interface DataSource {
  loaded: boolean;
  width?: number;
  height?: number;
  chart?: string;
};

export interface DataSources {
  genderSplit: GenderSplitDataSource;
  genderSplitPc: GenderSplitPcDataSource;
  livingWage: LivingWageDataSource;
  ghgEmissions: GHGEmissionsDataSource;
  payGap: PayGapDataSource;
  genderPayGap: GenderPayGapDataSource;
  bonuses: BonusesDataSource;
  boardGender: BoardGenderDataSource;
  boardDiversity: BoardDiversityDataSource;
  policies: PoliciesDataSource;
}
const initialDataState = {
  loaded: false
};

const initialDataSourcesState: DataSources = {
  genderSplit: initialDataState,
  genderSplitPc: initialDataState,
  livingWage: initialDataState,
  ghgEmissions: initialDataState,
  payGap: initialDataState,
  genderPayGap: initialDataState,
  bonuses: initialDataState,
  boardGender: initialDataState,
  boardDiversity: initialDataState,
  policies: initialDataState,
};

const type = 'ctl';

export const SDGImpactDownloadButton = ({
  surveyId,
  initiativeId,
  utrvStatuses,
  publicQuestionsOnly,
  totals,
  defaultStatus,
  ...downloadHandlers
}: Props) => {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(true);
  const [questionData, setQuestionData] = useState<ReportData[]>()
  const [dataSourcesState, setData] = useState(initialDataSourcesState);
  const [isLoaded, setLoaded] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const handleCloseModal = () => setOpenSettings(false);
  const handleOpenSettings = (e: React.MouseEvent<Element, MouseEvent>) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.blur();
    }
    setOpenSettings(true);
  };

  const analytics = getAnalytics();

  const startDownload = async () => {
    setDownloaded(false);
    setDownloading(true);
    try {
      const appliedStatus = downloadSettings.assuranceStatus ?? (downloadSettings.status || defaultStatus);
      const statuses = convertToUtrvStatuses(appliedStatus);
      const downloadScope: DownloadMultiScope = {
        scope: {
          [ScopeGroups.Frameworks]: [type],
          [ScopeGroups.Standards]: [],
          [ScopeGroups.Custom]: [],
          [ScopeGroups.Sdg]: [],
        },
        visibility: getDocVisibility(downloadSettings.visibility),
        targets: true, // Include targets
        ...statuses,
      };
      const data = await reportMultiScopeData(
        surveyId,
        downloadScope
      );
      setQuestionData(data);
    } catch (error) {
      console.log(error.message);
    }

    return analytics.track(AnalyticsEvents.SurveyDataDownloaded, {
      initiativeId,
      surveyId: surveyId,
      scopeValue: type,
      source: 'downloads_page',
      type: 'docx',
    })
  };

  const updateData = useCallback((key: string, data: DataSource) => {
    setData((dataSources) => ({
      ...dataSources,
      [key]: data
    }));
  }, [setData])

  useEffect(() => {
    const allLoaded = Object.keys(dataSourcesState).every(k => dataSourcesState[k as keyof DataSources].loaded);
    setLoaded(allLoaded);
  }, [dataSourcesState]);

  useEffect(() => {
    if (isLoaded && downloading) {
      setDownloading(false);
      G17Client.getSurveyListItem(surveyId)
        .then(survey => getSDGImpact(survey, dataSourcesState, { publicQuestionsOnly, utrvStatuses, displayUserInput: true }))
        .then(() => {
          setDownloaded(true);
          setData(initialDataSourcesState);
        })
        .catch(e => console.log(e.message));
    }
  }, [dataSourcesState, surveyId, isLoaded, downloading, utrvStatuses, publicQuestionsOnly])

  const [downloadSettings, setDownloadSettings] = useState<DownloadSettingsType>({
    selectedScopes: [], // selection not enabled since is fixed ctl
    visibility: VisibilityStatus.ExcludeData,
    status: UtrvStatus.Verified
  })

  const downloadModalProps: DownloadSettingPropType = {
    title: 'SDG Impact report',
    isOpen: openSettings,
    handleClose: handleCloseModal,
    downloadDoc: startDownload,
    onDownload: (type, config) => {
      return downloadReportHandler({ type, code: 'ctl', downloadSettings, ...downloadHandlers, config });
    },
    handleChange: (event) => handleSettingsChange(event, setDownloadSettings, downloadSettings),
    downloadSettings,
    totals,
  };

  return <div className='ctl-report-generator mt-5'>
    {!downloaded ? <Loader /> : <></>}
    {questionData && !isLoaded ? <GenerateCharts updateData={updateData} questionData={questionData} /> : <></>}
    <DownloadButton
      className='doc-btn'
      color='secondary'
      outline={true}
      onClick={handleOpenSettings}>
      Download report <i className='fas fa-file-download ml-2' />
    </DownloadButton>
    <DownloadSettingModal {...downloadModalProps}/>
  </div>
}
